/**
 * Click-to-load video lightbox.
 *
 * A bare iframe pulls roughly 1MB across 14 requests from four Google domains
 * on page load, before anyone presses play — and that cost multiplies on pages
 * with several videos. Each poster ships a self-hosted image instead; the
 * iframe is only created when a visitor opens the lightbox, and removed again
 * on close. Removing it (rather than just hiding the dialog) matters: YouTube
 * keeps streaming behind a hidden element, so this is what actually stops
 * playback and releases the memory.
 *
 * The lightbox itself is a native <dialog> (see base.njk), so focus trapping,
 * Esc-to-close and inerting the page behind it come from the platform.
 */
export function initVideoFacades(): void {
  const facades = document.querySelectorAll<HTMLButtonElement>('button[data-video-id]');
  if (facades.length === 0) return;

  const dialog = document.getElementById('video-lightbox') as HTMLDialogElement | null;
  const frame = document.getElementById('video-lightbox-frame');
  const title = document.getElementById('video-lightbox-title');
  const closeButton = document.getElementById('video-lightbox-close');
  if (!dialog || !frame || !title || typeof dialog.showModal !== 'function') return;

  function openVideo(videoId: string, videoTitle: string): void {
    const iframe = document.createElement('iframe');
    iframe.className = 'w-full h-full';
    // autoplay, because the click that opened the lightbox was the play intent.
    iframe.src = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1`;
    iframe.title = videoTitle;
    iframe.allow =
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    iframe.allowFullscreen = true;

    frame!.replaceChildren(iframe);
    title!.textContent = videoTitle;
    dialog!.showModal();
  }

  facades.forEach((facade) => {
    const videoId = facade.dataset.videoId;
    if (!videoId) return;
    facade.addEventListener('click', () => openVideo(videoId, facade.dataset.videoTitle ?? 'Video player'));
  });

  closeButton?.addEventListener('click', () => dialog.close());

  // Covers Esc and any other way the dialog closes, not just the button.
  dialog.addEventListener('close', () => frame!.replaceChildren());

  // Clicking the backdrop closes: the dialog fills the viewport, so any click
  // landing on the dialog itself rather than its contents is outside the frame.
  dialog.addEventListener('click', (event: MouseEvent) => {
    if (event.target === dialog) dialog.close();
  });
}
